import {
  PlaycastHostMessages,
  PlaycastMessage,
  PlaycastMessageHostPavilionLaunchReply,
  PlaycastMessageStateReplication,
  PlaycastMessageTarget,
  PlaycastStateMessages,
  generateHostMessage,
  HostMessageType,
  type HostScreenshotGrabCompletePayload,
  type HostMacroRecordingUploadCompletePayload,
} from '@playcastdotio/Messaging';
import { getElectronStores } from '../../stores/electronStores';
import { getSharedStores } from '../../stores/shared';
import { hostCampaignConfigState } from '../../stores/shared';
import { handlePlayjectorCrash } from '../../modules/events/onPlayjectorCrash';
import { addReplicationStateChangeCallback } from '@playcastdotio/StateStore';
import { ipcSend } from '../protocols/ipc';
import { stopStream } from '../../modules/peers/host';
import { playjectorStopPavilion, sendMatchmakerHostConfig } from '../../modules/managers/matchmaker';
import { campaignSwitchTracker } from '../../modules/managers/campaignSwitchTracker';
import { replicationRouter } from '../../../coordinators/ReplicationRouter';
import { appCoordinator, gamingScoreCoordinator, adminPeerHandler } from '../../../coordinators/init';
import { serviceContainer } from '../../../services/ServiceContainer';
import { playjectorMessageRouter } from '../../../services/MessageRouter';
import { USE_MESSAGE_ROUTER } from '../../../config/featureFlags';
import { handleScreenshotGrabCompleteIpcMessage } from '../../modules/realtime/handlers/host';
import { sendMessage as hostRealtimeSend } from '../../modules/realtime/index';
import { sendMessage as sendToNodeService } from '../../modules/nodeService';
import {
  clearPendingMacroRecordingUploadVideoKeysForTests,
  consumeMacroRecordingUploadVideoKey,
} from './macroRecordingUploadVideoKey';
import * as Sentry from '@sentry/react';

const electronState = getElectronStores();
const sharedStateStores = getSharedStores();

const STARTUP_GRACE_PERIOD_MS = 5000;
const appStartTime = Date.now();

/**
 * Take id of the most recently acknowledged macro-recorder recording start.
 * The native `encoderStatusReplyMSG` carries only a cameraName — no takeId — so
 * we correlate the encoder STARTED signal with the last take that reported
 * `controlStatus: started`. Cleared when that take reports finished/error.
 */
let lastMacroRecordingTakeId: string | null = null;

export { registerMacroRecordingUploadVideoKey } from './macroRecordingUploadVideoKey';
export { clearPendingMacroRecordingUploadVideoKeysForTests as _clearPendingMacroRecordingUploadVideoKeysForTests } from './macroRecordingUploadVideoKey';

/**
 * The encoder status reaches host-frontend via the replicated
 * `gameClipStatusState` store (host-electron consumes the raw
 * `encoderStatusReplyMSG` and replicates the state). Forward it to the
 * PlayjectorService so `waitForEncoderReady` waiters resolve, and push an
 * encoder-ready notification to the connected admin peer so the dashboard can
 * gate its initial frame capture on real encoder output.
 */
function handleEncoderStatusReplication(state: unknown): void {
  const encoderState = state as { cameraName?: string; result?: string } | undefined;
  const result = encoderState?.result;
  if (!result) return;

  serviceContainer.playjectorService.notifyEncoderStatus({
    cameraName: encoderState?.cameraName,
    result,
  });

  if (result.toLowerCase() === 'started' && lastMacroRecordingTakeId && adminPeerHandler) {
    Sentry.logger.info('[IPC] macro-recorder encoder STARTED — notifying admin peer', {
      feature: 'macro-recorder', takeId: lastMacroRecordingTakeId, cameraName: encoderState?.cameraName,
    });
    adminPeerHandler.sendUploadStatusNotification({
      takeId: lastMacroRecordingTakeId,
      status: 'started',
    });
  }
}

addReplicationStateChangeCallback('hostBackend', (message, splitReplicationKey) => {
  ipcSend(message);
});

export const handleIncomingMessages = (message: PlaycastMessage<PlaycastMessageTarget>) => {
  const target = message.header.target;
  const isReply = message.header.isReply;
  if (target === 'state') {
    const stateMessage = message as PlaycastMessage<PlaycastStateMessages>;
    const action = stateMessage.header.action;
    switch (action) {
      case 'replication':
        if (!isReply) {
          const validatedMessage = stateMessage as PlaycastMessage<PlaycastMessageStateReplication<keyof typeof electronState>>;
          if (!validatedMessage) {
            console.error('[IPC-Handler] Invalid replication message received');
            return;
          }

          appCoordinator.markSynced();

          const channel = validatedMessage.body.message.channel;
          const state = validatedMessage.body.message.state;

          // STRANGLER-FIG: Try routing to coordinator first
          const routedToCoordinator = replicationRouter.route(channel, state);

          if (routedToCoordinator) {
            console.log(`[IPC-Handler] ✓ Routed ${channel} to coordinator`);
            // Also update legacy store for backwards compatibility during migration
            // TODO-STRANGLER-FIG: Remove legacy store updates once coordinators are fully migrated
            if (electronState[channel]) {
              electronState[channel].updateState(state as never);
            } else if (sharedStateStores[channel]) {
              sharedStateStores[channel].updateState(state as never);
            }
          } else {
            // Fall back to legacy store-only update
            if (electronState[channel]) {
              electronState[channel].updateState(state as never);
            } else if (sharedStateStores[channel]) {
              sharedStateStores[channel].updateState(state as never);
            } else {
              console.error('[IPC-Handler] Invalid channel:', channel);
            }
          }

          // The replication message is typed against electronState keys, but
          // shared stores (e.g. gameClipStatusState) arrive on the same
          // channel at runtime — hence the widening cast.
          if ((channel as string) === 'gameClipStatusState') {
            handleEncoderStatusReplication(state);
          }
        }
        break;
    }
  }
  if (target === 'system' && isReply) {
    const action = message.header.action;
    if (action === 'launchElevatedProcess') {
      const body = message.body.message as { success?: boolean; processId?: number; error?: string };
      if (body.success) {
        console.log('[IPC] launchElevatedProcess reply: SUCCESS pid=%d', body.processId);
      } else {
        console.error('[IPC] launchElevatedProcess reply: FAILED error="%s"', body.error ?? 'unknown');
      }
    }
    if (action === 'launchElevatedPowerShellScript') {
      const body = message.body.message as { success?: boolean; processId?: number; error?: string };
      if (body.success) {
        console.log('[IPC] launchElevatedPowerShellScript reply: SUCCESS pid=%d', body.processId);
      } else {
        console.error('[IPC] launchElevatedPowerShellScript reply: FAILED error="%s"', body.error ?? 'unknown');
      }

      playjectorMessageRouter.route({ type: 'launchElevatedPowerShellScript', ...message.body.message } as any);
    }
    if (action === 'launchElevatedPowerShellScriptReceived') {
      console.log('[IPC] launchElevatedPowerShellScriptReceived ACK from Playjector');
      playjectorMessageRouter.route({
        type: 'launchElevatedPowerShellScriptReceived',
        ...message.body.message,
      } as any);
    }
    if (action === 'cancelProvisioningScript') {
      const body = message.body.message as { success?: boolean; wasCancelled?: boolean; error?: string };
      console.log(
        '[IPC] cancelProvisioningScript reply: success=%s wasCancelled=%s error=%s',
        body.success,
        body.wasCancelled,
        body.error ?? 'none',
      );
      playjectorMessageRouter.route({
        type: 'cancelProvisioningScript',
        ...message.body.message,
      } as any);
    }
    if (action === 'reboot') {
      playjectorMessageRouter.route({ type: 'reboot', ...message.body.message } as any);
    }
    if (action === 'scheduleReboot') {
      playjectorMessageRouter.route({ type: 'scheduleReboot', ...message.body.message } as any);
    }
    if (action === 'cancelScheduledReboot') {
      playjectorMessageRouter.route({ type: 'cancelScheduledReboot', ...message.body.message } as any);
    }
    if (action === 'getScoringMetrics') {
      console.log('[IPC] getScoringMetrics reply received — forwarding to GamingScoreCoordinator');
      if (USE_MESSAGE_ROUTER) {
        gamingScoreCoordinator.ingestMetricsFromIpc(
          message as PlaycastMessage<PlaycastMessageTarget>,
        );
      } else {
        console.warn(
          '[IPC] Prime mode (USE_MESSAGE_ROUTER=false) - raw metrics NOT submitted to realtime-api. ' +
            'Admin dashboard will show "no gaming score found" for this machine.',
        );
      }
    }
    if ((action as string) === 'getSoftwareInventory') {
      console.log('[IPC] getSoftwareInventory reply received — relaying to node-service');
      // Pure forwarder; node-service re-validates and owns dedup + upload.
      // queueOnFailure=false: this is a periodic "latest snapshot wins" poll
      // reply (~every 5 min) — if node-service is briefly offline, drop it
      // rather than queue it, so stale snapshots don't pile up and flush all
      // at once on reconnect. The next poll will send a fresher one anyway.
      sendToNodeService(message, { queueOnFailure: false });
    }
  }
  if (target === 'display') {
    console.log('[IPC] Display message received:', {
      action: message.header.action,
      isReply,
      body: message.body.message,
    });
  }
  if (target === 'process' && isReply) {
    // `macroStep` is not enumerated in the frozen PlaycastMessageTarget type
    // union (see libs/Messaging/src/types/messages/process.ts). Compare via a
    // string-narrowed view so TypeScript doesn't reject the literal.
    const action = message.header.action as string;
    if (action === 'macroStep') {
      // Phase 3: Stateful macro step reply from Playjector.
      // Forward the reply payload to PlayjectorService so the in-flight
      // `requestMacroStep` promise can resolve. The C++ schema lives in
      // SchemaList.cpp under processMacroStepReplyMSG and the body shape is
      // `{ result, variantResults: Record<captureId, { hash, width, height }>, error? }`.
      const body = message.body.message as {
        result?: string;
        variantResults?: Record<string, { hash: string; width: number; height: number }>;
        error?: string;
      };
      console.log('[IPC] processMacroStep reply received', {
        result: body.result,
        variantCount: body.variantResults ? Object.keys(body.variantResults).length : 0,
        error: body.error,
      });
      serviceContainer.playjectorService.notifyMacroStepResult({
        variantResults: body.variantResults ?? {},
        error: body.error,
      });
    }
    if (action === 'screenshotCapture') {
      const body = message.body.message as {
        result?: string;
        sessionName?: string;
        index?: number;
        filePath?: string;
        timestampMs?: number;
        error?: string;
      };
      console.log('[IPC] screenshotCapture reply received', {
        sessionName: body.sessionName,
        index: body.index,
        error: body.error,
      });
      Sentry.logger.info('[IPC] macro-recorder reply routed: screenshotCapture', {
        feature: 'macro-recorder', transport: 'ipc', direction: 'receive',
        action: 'screenshotCapture', sessionName: body.sessionName, index: body.index,
      });
      Sentry.addBreadcrumb({
        category: 'MACRO-SCREENSHOT',
        level: 'info',
        message: 'screenshotCapture reply crossed IPC (Playjector -> host-frontend)',
        data: { sessionName: body.sessionName, index: body.index, error: body.error },
      });
      serviceContainer.playjectorService.notifySessionScreenshotResult({
        result: body.result ?? '',
        sessionName: body.sessionName ?? '',
        index: body.index ?? 0,
        filePath: body.filePath ?? '',
        timestampMs: body.timestampMs ?? 0,
        error: body.error,
      });
    }
    if (action === 'scheduledScreenshot') {
      // Legacy: this action is no longer sent by Playjector. Scheduled screenshots
      // now route through processMacroStepMSG (action='macroStep') with
      // screenshotName='scheduled_latest'. Keeping this block as a no-op guard
      // until the old message path is fully removed from all branches.
      console.warn('[IPC] Received deprecated scheduledScreenshot reply — ignoring (replies now route via macroStep)');
    }
  }
  if (target === 'record' && isReply) {
    const action = message.header.action as string;
    if (action === 'controlStatus') {
      const body = message.body.message as {
        status?: string;
        takeId?: string;
        filePath?: string;
        error?: string;
      };
      console.log('[IPC] recordControlStatus reply received', {
        status: body.status,
        takeId: body.takeId,
        error: body.error,
      });
      Sentry.logger.info('[IPC] macro-recorder reply routed: recordControlStatus', {
        feature: 'macro-recorder', transport: 'ipc', direction: 'receive',
        action: 'recordControlStatus', takeId: body.takeId, status: body.status,
      });
      serviceContainer.playjectorService.notifyRecordControlStatus({
        status: body.status ?? '',
        takeId: body.takeId ?? '',
        filePath: body.filePath,
        error: body.error,
      });

      // Correlate the encoder STARTED signal (which carries no takeId) with the
      // take whose start was just acknowledged.
      if (body.status === 'started' && body.takeId) {
        lastMacroRecordingTakeId = body.takeId;
      } else if ((body.status === 'finished' || body.status === 'error') && body.takeId === lastMacroRecordingTakeId) {
        lastMacroRecordingTakeId = null;
      }
    } else if (action === 'statusQuery') {
      const body = message.body.message as { isRecording?: boolean; activeTakeId?: string };
      Sentry.logger.info('[IPC] macro-recorder reply routed: recordStatusQuery', {
        feature: 'macro-recorder', transport: 'ipc', direction: 'receive',
        action: 'statusQuery', isRecording: body.isRecording, activeTakeId: body.activeTakeId,
      });
      serviceContainer.playjectorService.notifyRecordStatusQuery({
        isRecording: body.isRecording,
        activeTakeId: body.activeTakeId,
      });
    }
  }
  if (target === 'host') {
    const hostMessage = message as PlaycastMessage<PlaycastHostMessages>;
    const action = hostMessage.header.action;
    switch (action) {
      case 'screenshotGrabComplete': {
        // host-electron's screenshot handler dispatches this IPC reply after
        // a presigned-PUT upload (success or failure). We forward the result
        // onward to realtime-api via WebSocket as HOST_SCREENSHOT_GRAB_COMPLETE
        // so the pending request there can resolve before its 30 s timeout.
        if (isReply) {
          handleScreenshotGrabCompleteIpcMessage(hostMessage);
        }
        break;
      }
      case 'scheduledScreenshotGrabComplete': {
        // Same as screenshotGrabComplete but for the scheduled screenshot path.
        // Forwards result to realtime-api as HOST_SCHEDULED_SCREENSHOT_GRAB_COMPLETE.
        if (isReply) {
          const result = hostMessage.body?.message as HostScreenshotGrabCompletePayload;
          Sentry.logger.info(
            `[Scheduled Screenshot Grab] IPC reply received from host-electron (success=${result.success})`,
            {
              requestId: result.requestId,
              success: result.success,
            },
          );
          const wsMessage = generateHostMessage(HostMessageType.HOST_SCHEDULED_SCREENSHOT_GRAB_COMPLETE, result);
          hostRealtimeSend(wsMessage);
        }
        break;
      }
      case 'notifyPlayjectorDisconnect': {
        if (!isReply) {
          const isStartupDisconnect = Date.now() - appStartTime < STARTUP_GRACE_PERIOD_MS;

          if (isStartupDisconnect) {
            console.warn('[IPC] Playjector disconnected during startup grace period — ignoring as transient');
          } else {
            console.log('[IPC] notifyPlayjectorDisconnect received - Playjector crashed or disconnected');
          }

          Sentry.captureMessage('[GUEST_MODE] Playjector disconnected unexpectedly', {
            level: isStartupDisconnect ? 'warning' : 'error',
            tags: {
              event_type: 'playjector_disconnect',
              startup_disconnect: String(isStartupDisconnect),
              guest_mode: String(sharedStateStores.hostMatchmakingState.currentState.guestAccount),
              pavilion_mode: String(sharedStateStores.hostState.currentState.edition === 'pavilion'),
              ad_mode: String(sharedStateStores.adModeState.currentState.enabled),
            },
            contexts: {
              guest_mode: {
                guestAccount: sharedStateStores.hostMatchmakingState.currentState.guestAccount,
                matchmakeable: sharedStateStores.hostMatchmakingState.currentState.matchmakeable,
                edition: sharedStateStores.hostState.currentState.edition,
                hostStatus: sharedStateStores.hostState.currentState.status,
                adModeEnabled: sharedStateStores.adModeState.currentState.enabled,
                gameLaunched: sharedStateStores.playjectorHostModeState.currentState.gameLaunched,
                userLoggedIn: sharedStateStores.playjectorHostModeState.currentState.userLoggedIn,
                steamLaunched: sharedStateStores.playjectorHostModeState.currentState.steamLaunched,
                uptimeMs: Date.now() - appStartTime,
              },
            },
          });

          if (!isStartupDisconnect) {
            handlePlayjectorCrash();
          }
          // App.mainWindow.webContents.send('notifyPlayjectorDisconnect');
        }
        break;
      }
      case 'pavilionLaunch': {
        if (!isReply) {
          break;
        }
        const validatedMessage = hostMessage as PlaycastMessage<PlaycastMessageHostPavilionLaunchReply>;
        if (!validatedMessage) {
          console.error('[IPC] Invalid pavilionLaunch reply received');
          return;
        }

        const result = validatedMessage.body.message.result;
        const reason = validatedMessage.body.message.reason;

        console.log('[IPC] Pavilion launch reply received:', { result, reason });

        // Handle security failures (existing behavior)
        if (result === 'SECURITY_FAILURE' || result === 'PATH_DISCREPANCY' || result === 'PID_DISCREPANCY') {
          console.error('[IPC] Major security failure in pavilion mode:', result);

          // Send Sentry diagnostic for security failure
          Sentry.captureMessage(`[GUEST_MODE] Pavilion security failure: ${result}`, {
            level: 'error',
            tags: {
              event_type: 'guest_mode_security_failure',
              failure_type: result,
              guest_mode: String(sharedStateStores.hostMatchmakingState.currentState.guestAccount),
              ad_mode: String(sharedStateStores.adModeState.currentState.enabled),
            },
            contexts: {
              security_failure: {
                result,
                guestAccount: sharedStateStores.hostMatchmakingState.currentState.guestAccount,
                matchmakeable: sharedStateStores.hostMatchmakingState.currentState.matchmakeable,
                edition: sharedStateStores.hostState.currentState.edition,
                adModeEnabled: sharedStateStores.adModeState.currentState.enabled,
                gameLaunched: sharedStateStores.playjectorHostModeState.currentState.gameLaunched,
                userLoggedIn: sharedStateStores.playjectorHostModeState.currentState.userLoggedIn,
              },
            },
          });

          playjectorStopPavilion();
          stopStream();
        }

        // CRITICAL: Notify new coordinator architecture (if enabled)
        // This ensures pavilionLaunchReply is routed to GameLaunchCoordinator → PavilionCoordinator → API
        if (USE_MESSAGE_ROUTER) {
          console.log('[IPC] 🎯 Notifying new coordinator architecture of pavilionLaunchReply:', result);
          const playjectorService = serviceContainer.playjectorService;
          playjectorService.notifyCampaignLaunchResult({
            result: result as 'SUCCESS' | 'FAILURE',
            message: reason,
          });
          // When using coordinators, they handle ALL launch result logic including timeouts
          // Skip legacy cleanup to prevent coordinator state from being overwritten
          console.log('[IPC] ✅ Coordinator notified - skipping legacy cleanup logic');
          return;
        }

        // === LEGACY SYSTEM ONLY (USE_MESSAGE_ROUTER = false) ===
        // Notify campaign switch tracker if there's a pending switch (legacy system)
        if (campaignSwitchTracker.hasPendingSwitch()) {
          console.log('[IPC] Notifying campaign switch tracker of result:', result);
          campaignSwitchTracker.reportResult(result, reason);
        } else {
          // CRITICAL: If we receive ANY result but no pending switch (timeout already fired),
          // we need to ensure the host state is cleaned up to prevent getting stuck
          console.warn(`[IPC] Received pavilionLaunchReply (${result}) with no pending switch - late message after timeout`);

          // This can happen in two scenarios:
          // 1. Late FAILURE: Playjector reports failure after client timeout fired
          // 2. Late SUCCESS: Playjector reports success after client timeout fired (BUG!)
          //
          // In BOTH cases, the timeout already:
          // - Cleared campaignSwitchTracker
          // - Reported failure to API
          // - Recorded blacklist entry
          //
          // We must clear the host state regardless of result to prevent:
          // - Getting stuck on a campaign we've blacklisted
          // - Reporting "ready" for a campaign that timed out

          // Check current state to distinguish between:
          // 1. Duplicate SUCCESS for current running campaign (ignore)
          // 2. Late SUCCESS for timed-out/blacklisted campaign (terminate game)
          const currentGameState = sharedStateStores.hostMatchmakingState.currentState.gameState;
          const currentCampaignId = sharedStateStores.hostMatchmakingState.currentState.campaignId;

          if (result === 'SUCCESS' && currentGameState === 'ready' && currentCampaignId) {
            // This is a duplicate SUCCESS message for the campaign that's already running
            console.log('[IPC] Duplicate SUCCESS message for current running campaign - ignoring');
            console.log(`[IPC] Current campaign: ${currentCampaignId}, gameState: ${currentGameState}`);
            return; // Don't terminate, don't clear state
          }

          // If we reach here, this is a late message for a campaign we've abandoned
          console.log(`[IPC] Late ${result} message for abandoned campaign (timeout already handled this):`, reason);
          console.log(`[IPC] Current state: campaignId=${currentCampaignId}, gameState=${currentGameState}`);

          // CRITICAL: If this is a late SUCCESS, Playjector has actually launched the game
          // But we've already blacklisted this campaign due to timeout
          // We need to terminate the game so Playjector's state doesn't conflict with our cleared state
          if (result === 'SUCCESS') {
            console.log('[IPC] Late SUCCESS for abandoned campaign - terminating game that we already blacklisted');
            playjectorStopPavilion(); // Terminate the game in Playjector
          }

          // Clear any stuck operational state (API manages orchestration state)
          sharedStateStores.hostMatchmakingState.updateState({
            ...sharedStateStores.hostMatchmakingState.currentState,
            campaignId: undefined,
            gameState: 'idle',
            matchmakeable: true,
          });

          // Clear campaign config
          hostCampaignConfigState.updateState({
            ...hostCampaignConfigState.currentState,
            campaignId: undefined,
            launchPackage: undefined,
            restartOnDisconnect: undefined,
          });

          // Send updated state to realtime-API
          sendMatchmakerHostConfig().catch((error) => {
            console.error('[IPC] Failed to send updated matchmaker config after clearing stuck state:', error);
          });

          console.log('[IPC] Late message handled - host is now idle and ready for reassignment');
        }

        break;
      }
    }
    // macroRecordingUploadComplete / screenshotUploadComplete are not in the
    // frozen PlaycastHostMessages action union; handle via string comparison
    // after the typed switch.
    const hostAction = message.header.action as string;
    if (hostAction === 'macroRecordingUploadComplete' && isReply) {
      const result = message.body.message as unknown as HostMacroRecordingUploadCompletePayload;
      Sentry.logger.info(
        `[Macro Recording Upload] macro-recorder IPC reply received from host-electron (success=${result.success})`,
        { feature: 'macro-recorder', takeId: result.takeId, requestId: result.requestId, success: result.success },
      );
      const wsMessage = generateHostMessage(HostMessageType.HOST_MACRO_RECORDING_UPLOAD_COMPLETE, result);
      hostRealtimeSend(wsMessage);

      // G13: consume the pending key UNCONDITIONALLY — must not be gated on
      // adminPeerHandler existing, otherwise an admin disconnect mid-upload
      // leaves the key pending until cap eviction.
      const consumedKey = consumeMacroRecordingUploadVideoKey(result.requestId);
      if (adminPeerHandler) {
        // The key is only surfaced to the admin on success — a failed upload
        // has no video to reference.
        adminPeerHandler.sendUploadStatusNotification({
          takeId: result.takeId,
          status: result.success ? 'complete' : 'failed',
          error: result.success ? undefined : result.error,
          videoKey: result.success ? consumedKey : undefined,
        });
      }
    }
    if (hostAction === 'screenshotUploadComplete' && isReply) {
      const result = message.body.message as unknown as {
        requestId: string; success: boolean; sizeBytes?: number; error?: string;
      };
      Sentry.logger.info(
        `[Screenshot Upload] macro-recorder IPC reply received from host-electron (success=${result.success})`,
        { feature: 'macro-recorder', requestId: result.requestId, success: result.success },
      );
      try {
        serviceContainer.recordingControlManager.notifyScreenshotUploadComplete(result);
      } catch (err) {
        console.warn('[IPC] recordingControlManager not registered yet — dropping screenshotUploadComplete', {
          requestId: result.requestId,
        });
      }
    }
  }
};
